/**
 * AssignmentConfigSettings — Phase 11 Sprint 11-S3
 * Lets admins control auto-assignment toggle, confidence thresholds, and factor weights.
 *
 * Validation (client-side mirrors backend):
 *   assisted threshold < auto threshold
 *   factorWeights sum === 100
 * A ConfirmModal fires before every save.
 */

import React, { useState, useEffect } from 'react';
import colors from '../../styles/colors';
import { getAssignmentConfig, updateAssignmentConfig } from '../../utils/api';

const DEFAULT_CONFIG = {
  autoAssignEnabled: true,
  confidenceThreshold: { auto: 85, assisted: 70 },
  factorWeights: { specialisation: 30, availability: 25, load: 20, continuity: 15, tier: 10 },
};

const FACTOR_LABELS = {
  specialisation: 'Specialisation match',
  availability:   'Availability',
  load:           'Healer load',
  continuity:     'Patient–healer continuity',
  tier:           'Healer tier',
};

const FACTOR_KEYS = Object.keys(FACTOR_LABELS);

const styles = {
  container: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '24px 16px',
    fontFamily: 'inherit',
  },
  heading: {
    fontSize: 20,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 24,
  },
  card: {
    background: colors.backgroundCard,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 8,
    padding: 20,
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: 12,
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    minHeight: 44,
  },
  toggleTrack: (enabled) => ({
    width: 48,
    height: 26,
    borderRadius: 13,
    background: enabled ? colors.brandPrimary : colors.borderStrong,
    position: 'relative',
    cursor: 'pointer',
    transition: 'background 0.2s',
    flexShrink: 0,
    border: 'none',
    padding: 0,
    outline: 'none',
    minWidth: 48,
    minHeight: 44,
    display: 'flex',
    alignItems: 'center',
  }),
  toggleThumb: (enabled) => ({
    position: 'absolute',
    left: enabled ? 24 : 2,
    top: '50%',
    transform: 'translateY(-50%)',
    width: 20,
    height: 20,
    borderRadius: '50%',
    background: colors.backgroundCard,
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
    transition: 'left 0.2s',
  }),
  toggleLabel: {
    fontSize: 14,
    color: colors.textPrimary,
  },
  fieldRow: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: 14,
  },
  label: {
    fontSize: 13,
    color: colors.textSecondary,
    marginBottom: 4,
  },
  input: {
    padding: '8px 10px',
    fontSize: 15,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 6,
    color: colors.textPrimary,
    background: colors.backgroundCard,
    width: '100%',
    boxSizing: 'border-box',
    minHeight: 44,
  },
  factorRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    gap: 12,
  },
  factorLabel: {
    flex: 1,
    fontSize: 14,
    color: colors.textPrimary,
  },
  factorInput: {
    width: 72,
    padding: '8px 10px',
    fontSize: 15,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 6,
    color: colors.textPrimary,
    background: colors.backgroundCard,
    textAlign: 'right',
    minHeight: 44,
  },
  sumRow: (ok) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    fontSize: 13,
    color: ok ? colors.statusSuccess : colors.statusDanger,
    fontWeight: 600,
    marginTop: 4,
  }),
  errorText: {
    fontSize: 13,
    color: colors.statusDanger,
    marginBottom: 12,
  },
  saveBtn: (canSave) => ({
    display: 'block',
    width: '100%',
    padding: '12px 0',
    fontSize: 15,
    fontWeight: 600,
    background: canSave ? colors.brandPrimary : colors.disabled,
    color: canSave ? colors.textOnBrand : colors.disabledText,
    border: 'none',
    borderRadius: 8,
    cursor: canSave ? 'pointer' : 'not-allowed',
    minHeight: 48,
  }),
  // ConfirmModal overlay
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 500,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  modal: {
    background: colors.backgroundCard,
    borderRadius: 10,
    padding: 28,
    maxWidth: 360,
    width: '90%',
    zIndex: 600,
    boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: colors.textPrimary,
    marginBottom: 10,
  },
  modalBody: {
    fontSize: 14,
    color: colors.textSecondary,
    marginBottom: 20,
  },
  modalButtons: {
    display: 'flex',
    gap: 10,
    justifyContent: 'flex-end',
  },
  cancelBtn: {
    padding: '10px 18px',
    fontSize: 14,
    border: `1px solid ${colors.borderDefault}`,
    borderRadius: 6,
    background: colors.backgroundCard,
    color: colors.textPrimary,
    cursor: 'pointer',
    minHeight: 44,
  },
  confirmBtn: {
    padding: '10px 18px',
    fontSize: 14,
    fontWeight: 600,
    border: 'none',
    borderRadius: 6,
    background: colors.brandPrimary,
    color: colors.textOnBrand,
    cursor: 'pointer',
    minHeight: 44,
  },
};

function ConfirmModal({ onConfirm, onCancel }) {
  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.modalTitle}>Save assignment config?</div>
        <div style={styles.modalBody}>
          This will update how healers are automatically assigned to new cases. The change takes effect immediately.
        </div>
        <div style={styles.modalButtons}>
          <button style={styles.cancelBtn} onClick={onCancel}>Cancel</button>
          <button style={styles.confirmBtn} onClick={onConfirm}>Save changes</button>
        </div>
      </div>
    </div>
  );
}

export default function AssignmentConfigSettings() {
  const [form, setForm]                 = useState(DEFAULT_CONFIG);
  const [loading, setLoading]           = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [savedOk, setSavedOk]           = useState(false);
  const [error, setError]               = useState('');
  const [showConfirm, setShowConfirm]   = useState(false);

  useEffect(() => {
    async function fetchConfig() {
      try {
        const res = await getAssignmentConfig();
        const cfg = res.data?.data?.assignmentConfig;
        if (cfg && Object.keys(cfg).length > 0) {
          setForm({
            autoAssignEnabled:   cfg.autoAssignEnabled   ?? DEFAULT_CONFIG.autoAssignEnabled,
            confidenceThreshold: { ...DEFAULT_CONFIG.confidenceThreshold, ...cfg.confidenceThreshold },
            factorWeights:       { ...DEFAULT_CONFIG.factorWeights,       ...cfg.factorWeights },
          });
        }
      } catch {
        // silently use defaults — non-critical on load
      } finally {
        setLoading(false);
      }
    }
    fetchConfig();
  }, []);

  const weightsSum = FACTOR_KEYS.reduce((acc, k) => acc + (Number(form.factorWeights[k]) || 0), 0);
  const thresholdValid = Number(form.confidenceThreshold.assisted) < Number(form.confidenceThreshold.auto);
  const canSave = weightsSum === 100 && thresholdValid && !isSubmitting;

  function handleToggle() {
    setForm(f => ({ ...f, autoAssignEnabled: !f.autoAssignEnabled }));
  }

  function handleThreshold(field, value) {
    setForm(f => ({
      ...f,
      confidenceThreshold: { ...f.confidenceThreshold, [field]: value },
    }));
  }

  function handleWeight(key, value) {
    setForm(f => ({
      ...f,
      factorWeights: { ...f.factorWeights, [key]: value },
    }));
  }

  function handleSaveClick() {
    if (!canSave) return;
    setShowConfirm(true);
  }

  async function handleConfirm() {
    setShowConfirm(false);
    setIsSubmitting(true);
    setSavedOk(false);
    setError('');
    try {
      await updateAssignmentConfig({
        autoAssignEnabled:   form.autoAssignEnabled,
        confidenceThreshold: {
          auto:     Number(form.confidenceThreshold.auto),
          assisted: Number(form.confidenceThreshold.assisted),
        },
        factorWeights: Object.fromEntries(
          FACTOR_KEYS.map(k => [k, Number(form.factorWeights[k])])
        ),
      });
      setSavedOk(true);
      // Re-enable after 3 s so the admin can make further edits without a page reload.
      setTimeout(() => { setIsSubmitting(false); setSavedOk(false); }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save. Please try again.');
      setIsSubmitting(false);
    }
  }

  if (loading) {
    return <div style={{ padding: 24, color: colors.textSecondary }}>Loading config…</div>;
  }

  return (
    <div style={styles.container}>
      {showConfirm && (
        <ConfirmModal onConfirm={handleConfirm} onCancel={() => setShowConfirm(false)} />
      )}

      <div style={styles.heading}>Healer Assignment Config</div>
      <div style={styles.subtitle}>
        Controls how LifeChiAI auto-assigns healers to new cases.
      </div>

      {/* Card 1 — Auto-assignment toggle */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Auto-Assignment</div>
        <div style={styles.toggleRow}>
          <button
            role="switch"
            aria-checked={form.autoAssignEnabled}
            style={styles.toggleTrack(form.autoAssignEnabled)}
            onClick={handleToggle}
            aria-label="Enable auto-assignment"
          >
            <div style={styles.toggleThumb(form.autoAssignEnabled)} />
          </button>
          <span style={styles.toggleLabel}>
            {form.autoAssignEnabled ? 'Auto-assignment enabled' : 'Auto-assignment disabled'}
          </span>
        </div>
      </div>

      {/* Card 2 — Confidence thresholds */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Confidence Thresholds</div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Auto-assign threshold (%)</label>
          <input
            type="number"
            style={styles.input}
            min={50}
            max={100}
            value={form.confidenceThreshold.auto}
            onChange={e => handleThreshold('auto', e.target.value)}
          />
        </div>
        <div style={styles.fieldRow}>
          <label style={styles.label}>Assisted-assign threshold (%)</label>
          <input
            type="number"
            style={styles.input}
            min={30}
            max={99}
            value={form.confidenceThreshold.assisted}
            onChange={e => handleThreshold('assisted', e.target.value)}
          />
        </div>
        {!thresholdValid && (
          <div style={styles.errorText}>
            Assisted threshold must be less than auto-assign threshold.
          </div>
        )}
      </div>

      {/* Card 3 — Factor weights */}
      <div style={styles.card}>
        <div style={styles.cardTitle}>Factor Weights (must sum to 100)</div>
        {FACTOR_KEYS.map(key => (
          <div key={key} style={styles.factorRow}>
            <span style={styles.factorLabel}>{FACTOR_LABELS[key]}</span>
            <input
              type="number"
              style={styles.factorInput}
              min={0}
              max={100}
              value={form.factorWeights[key]}
              onChange={e => handleWeight(key, e.target.value)}
            />
          </div>
        ))}
        <div style={styles.sumRow(weightsSum === 100)}>
          Sum: {weightsSum} / 100
        </div>
      </div>

      {error && <div style={styles.errorText}>{error}</div>}

      <button
        style={styles.saveBtn(canSave)}
        onClick={handleSaveClick}
        disabled={!canSave}
      >
        {savedOk ? 'Saved' : 'Save changes'}
      </button>
    </div>
  );
}
